import React, { useEffect, useState } from 'react';
import { RootState } from '../../../reducers';
import { bindActionCreators, Dispatch } from 'redux';
import * as actions from '../../../actions';
import * as _actionsSearch from '../../../actions/search';
import * as _actionsProduct from '../../../actions/product';
import * as _actionsFilters from '../../../actions/filters';
import * as _actionsJourney2 from '../../../actions/journey2';
import * as _actionsBasket from '../../../actions/basket';
import { withRouter } from '../../../utils/withRouter';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import classNames from 'classnames';
import Spinner from '../../Spinner';
import { Button, EButtonType } from '@repo/apg-ui';
import { getParameterByName } from '../../../utils/toSearchParams';
import { ReactComponent as RedX, ReactComponent as RedxIcon } from '../../../assets/icons/redX.svg';
import './ProductImage.scss';
import { createDateRange, parseToApiDate } from '../../../utils/react';
import { parseISO } from 'date-fns';
import { prepareAddToBasketPayload } from '../../../utils/payloads';
import { createBasket, removeFromBasket } from '../../../utils/basket';
import ErrorContainer from '../../ErrorContainer/ErrorContainer';
import { defined } from '../../../utils/variableEvaluation';
import { ReactComponent as Check } from '../../../assets/icons/check.svg';
import { useNavigate } from 'react-router';
import {
  SchemaAddToBasketResponse,
  SchemaNewBasketResponse,
  SchemaProduct,
  SchemaPossibleBookingDates,
} from '@repo/legacy-commerce-api';
import { PartiallyIntegratedUserDataEC } from '../../../reducers/app';

interface Props {
  location: any;
  actionsProduct: typeof _actionsProduct;
  userDataEC: PartiallyIntegratedUserDataEC;
  intl: any;
  actionsJourney2: typeof _actionsJourney2;
  possibleBookingDates: SchemaPossibleBookingDates;
  actionsBasket: typeof _actionsBasket;
  errors: any;
  notices: any;
  productsJ2ByUrn: Record<string, SchemaProduct>;
}

const ProductImage = ({
  location,
  actionsProduct,
  userDataEC,
  intl,
  actionsJourney2,
  possibleBookingDates,
  actionsBasket,
  errors,
  notices,
  productsJ2ByUrn,
}: Props) => {
  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState([
    createDateRange(new Date(userDataEC.startDate) || new Date(), userDataEC.duration || 7, {
      toEndOfYear: false,
      fullWeeks: true,
    }),
  ]);
  const [duration, setDuration] = useState(userDataEC.duration || 7);
  const [isInBasket, setIsInBasket] = useState(true);

  const { ui, basketProducts } = userDataEC;
  const defaultProduct: SchemaProduct = {
    name: '',
    state: null,
    mediacount: 2,
    productphotopath: '',
    formatcode: '',
    producttype: '',
    additionalinfo: { restriction: { codes: [] } },
    producturn: '',
    address: '',
    availabilities: [],
    area: [],
    format: '',
    mediainfo: {
      // @ts-expect-error mismatch with SchemaProduct, ignore:
      nummer: null,
    },
  };

  const [product, setProduct] = useState(defaultProduct);
  const navigate = useNavigate();

  const isHideActions = getParameterByName('isHideActions', location.search) === 'true';

  useEffect(() => {
    const _urn = getParameterByName('urn', location.search);
    if (_urn) {
      let startRangeDate: Date;
      if (userDataEC.startDate) {
        startRangeDate = new Date(userDataEC.startDate);
      } else if (possibleBookingDates && possibleBookingDates['start']) {
        startRangeDate = parseISO(possibleBookingDates['start'].date);
      } else {
        startRangeDate = new Date();
      }

      let initialDuration = userDataEC.duration || 7;
      actionsProduct
        .fetchProductByUrn({ urn: encodeURI(_urn), date: parseToApiDate(startRangeDate) })
        .then((res: any) => {
          // should be SchemaProductResponse -> OpenAPI model is not clean enough, includes unknown.
          const isProductInBasket =
            Object.keys(basketProducts.productsByUrn).includes(res.data.producturn) && basketProducts.products;
          if (isProductInBasket) {
            setIsInBasket(true);
            startRangeDate = parseISO(basketProducts.productsByUrn[res.data.producturn].startdate);
            setProduct(basketProducts.productsByUrn[res.data.producturn]);
            // @ts-expect-error Open API spec mismatch: duration does not exist on type:
            initialDuration = basketProducts.productsByUrn[res.data.producturn].duration;
          } else {
            res.data.startdate = startRangeDate;
            setIsInBasket(false);
            setProduct(res.data);
          }

          setDuration(initialDuration);
          const range = createDateRange(startRangeDate, initialDuration, {
            toEndOfYear: false,
            fullWeeks: true,
          });
          setDateRange([range]);
          setIsLoading(false);
        })
        .catch((error: any) => {
          console.log(error);
        });
    }
  }, [location]);

  const addToBasket = (basketData: { basket: number; basketreference: string }) => {
    const { basket, basketreference } = basketData;
    let payload;
    payload = prepareAddToBasketPayload(
      basket,
      basketreference,
      parseToApiDate(dateRange[0].startDate),
      duration,
      product.producturn,
      userDataEC.industry,
    );

    // This should be async/await in Fusion:
    return actionsBasket
      .addToBasket(payload)
      .then((res: SchemaAddToBasketResponse) => {
        if (res) {
          actionsBasket
            .getBasketContent(basketData)
            .then(() => {
              setIsLoading(false);
              closeModal();
            })
            .catch((err: any) => {
              console.error(err);
              setIsLoading(false);
            });
        } else {
          setIsLoading(false);
        }
      })
      .catch(() => {
        setIsLoading(false);
      });
  };

  const addOrCreateBasket = () => {
    setIsLoading(true);
    if (userDataEC.basketInfo) {
      if (isInBasket) {
        return removeFromBasket(
          // @ts-expect-error Open API spec mismatch: basketentryid does not exist on type (basketentryId does, capital I):
          userDataEC.basketProducts.productsByUrn[product.producturn].basketentryid,
          userDataEC,
          actionsBasket,
          product.producturn,
        ).then(() => {
          return addToBasket(userDataEC.basketInfo);
        });
      } else {
        return addToBasket(userDataEC.basketInfo);
      }
    } else {
      return createBasket(userDataEC, dateRange[0].startDate, actionsBasket).then((res: SchemaNewBasketResponse) => {
        return addToBasket({
          basket: res.data.basket,
          basketreference: res.data.basketreference,
        });
      });
    }
  };

  const closeModal = () => {
    setProduct(defaultProduct);
    setIsInBasket(false);
    const redirect = getParameterByName('redirect', location.search);
    if (redirect === 'product') {
      navigate({
        search: `?modalContent=product&urn=${product?.producturn}${isHideActions && '&isHideActions=true'}`,
      });
    } else {
      navigate({ search: '' });
    }
  };

  const getProductState = () => {
    return defined(productsJ2ByUrn[product?.producturn]?.state)
      ? productsJ2ByUrn[product.producturn].state
      : product.state;
  };

  const productState = getProductState();

  return (
    <div
      className={classNames('productImageModal', {
        modalLoading: isLoading,
      })}
      id='root'
    >
      {isLoading && <Spinner />}
      <div className={classNames('modalHeader border-bottom d-flex justify-content-between align-items-start h-auto')}>
        <div className='d-flex flex-column justify-content-center gap-2'>
          <div className='font-24 font-bold'>
            {product?.format ? product.format + ' - ' + product?.name : product?.name}
          </div>
          <div className='d-flex justify-content-start align-items-center'>
            {product?.mediainfo?.nummer && (
              <div className='product-id'>
                <div className='badge bg-light product-code'>
                  <span>ID: {product?.mediainfo?.nummer}</span>
                </div>
              </div>
            )}

            {productState >= 0 && (
              <div className='product-state'>
                <div className='d-flex align-items-center'>
                  {productState === 0 && userDataEC?.startDate && (
                    <div className='d-flex justify-content-start ms-4 availability'>
                      <span className='green-checker ps-0'>
                        <Check />
                      </span>
                      <span className='ps-0 font-14 font-600'>
                        {intl.formatMessage({
                          id: 'ec.journey02.available',
                          defaultMessage: 'Verfügbar',
                        })}
                      </span>
                    </div>
                  )}
                  {(productState > 0 || !userDataEC?.startDate) && (
                    <div className='d-flex justify-content-start ms-4 availability'>
                      <span className='unavailable-checker ps-0'>
                        <RedX />
                      </span>

                      <span className='ps-0 font-14 font-600'>
                        {intl.formatMessage({
                          id: 'ec.journey02.unavailable',
                          defaultMessage: 'Nicht verfügbar',
                        })}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className='close-modal align-items-start justify-content-start'>
          <Button onClick={closeModal} className='mt-0'>
            <RedxIcon />
          </Button>
        </div>
      </div>

      <div className={classNames('modalBody h-100')}>
        <div className='product-image-wrapper'>
          {(errors || notices) && (
            <ErrorContainer
              getDataFromRedux={true}
              journeyType={'journey02'}
              classNameErrorWrapper={'mw-100 m-0 mb-3'}
              classNameErrorContainer={'m-0'}
            />
          )}
          <div
            className={classNames('product-image mb-3', {
              'product-loading': isLoading,
            })}
          >
            {product?.productphotopath && (
              <img alt='Product' src={product?.productphotopath + '&resolution=preview?'} />
            )}
          </div>
        </div>
      </div>

      <div className={classNames('modalFooter border-top d-flex w-100 h-auto bg-white flex-column')}>
        <div
          className={classNames('d-flex align-items-center', {
            'justify-content-end': !ui.isMobile,
            'justify-content-between': ui.isMobile,
          })}
        >
          <Button buttonType={EButtonType.BasicLink} className='mt-0 ' onClick={closeModal}>
            <FormattedMessage id='ec.components.buttons.cancel' defaultMessage='Abbrechen' />
          </Button>

          {!isHideActions && (
            <Button
              buttonType={EButtonType.InvertedPillMedium}
              className='mt-0 ms-24 text-uppercase'
              isDisabled={isLoading || errors.length > 0 || notices.length > 0 || !userDataEC?.startDate}
              onClick={() => {
                addOrCreateBasket();
              }}
            >
              {isInBasket ? (
                <FormattedMessage id='ec.components.buttons.updateInBasket' defaultMessage='Update' />
              ) : (
                <FormattedMessage id='ec.components.buttons.addToBasket' defaultMessage='Zum Warenkorb hinzufügen' />
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

function mapStateToProps(state: RootState, ownProps: any) {
  return {
    ...ownProps,
    ...state.app,
    ...state.router,
    location: state.router.location,
  };
}

function mapDispatchToProps(dispatch: Dispatch<actions.ACTION>) {
  return {
    actionsSearch: bindActionCreators(_actionsSearch as any, dispatch),
    actionsJourney2: bindActionCreators(_actionsJourney2 as any, dispatch),
    actionsProduct: bindActionCreators(_actionsProduct as any, dispatch),
    actionsFilters: bindActionCreators(_actionsFilters as any, dispatch),
    actionsBasket: bindActionCreators(_actionsBasket as any, dispatch),
    dispatch: dispatch,
  };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(ProductImage)));
